import React, { useState, useEffect } from "react";
import { Modal, Button, Form } from "react-bootstrap";
import "bootstrap/dist/css/bootstrap.min.css";
import { useTranslate } from "../../hooks/LanguageProvider";
import { toast } from "react-toastify";

interface ModalEditSiteProps {
  show: boolean;
  onHide: () => void;
  id_site: number | null;
  site?: any | null;
  onSuccess?: () => void;
}

const backendUrl = process.env.REACT_APP_BACKEND_URL;

const ModalEditSite: React.FC<ModalEditSiteProps> = ({
  show,
  onHide,
  id_site,
  site,
  onSuccess,
}) => {
  const [formData, setFormData] = useState({
    name: "",
    location: "",
  });

  const [loading, setLoading] = useState(false);
  const { translate } = useTranslate();

  // Fetch site data when modal opens
  const getSiteById = async (id_site: number) => {
    try {
      const response = await fetch(
        `${backendUrl}/api/site/details/${id_site}`,
        {
          method: "GET",
          credentials: "include",
          headers: { "Content-Type": "application/json" },
        }
      );

      if (!response.ok) {
        throw new Error("Error retrieving site");
      }

      const site = await response.json();
      return site || null;
    } catch (error) {
      console.error("Error retrieving site:", error);
      return null;
    }
  };

  useEffect(() => {
    //if parent passed site object, use immediately
    if (show && site) {
      const s = Array.isArray(site) ? site[0] : site;
      setFormData({ name: s?.name || "",
        location: s?.location || "", });
        return;}
        //else fall back to fetching by id_site, fetch it as a fallback
        if (show && id_site) {
      setFormData({ name: "", location: "" });
      getSiteById(id_site).then((fetched) => {
        const s = Array.isArray(fetched) ? fetched[0] : fetched;
        if (s) {
          setFormData({
            name: s.name || "",
            location: s.location || "",
          });
        }
      });
    }
    if (!show) {
      setFormData({ name: "", location: "" });
    }
  }, [show,site, id_site]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { id, value } = e.target;
    setFormData((prevState) => ({
      ...prevState,
      [id]: value,
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const targetId = site?.id_site || id_site;
    if (!targetId) return;
    setLoading(true);
    try {
      const response = await fetch(`${backendUrl}/api/site/update/${targetId}`, {
        method: "PUT",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(formData),
      });
      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(errorText || "Error updating site");
      }
      toast.success(translate("Site updated successfully!"), { position: "bottom-right", autoClose: 3000 });
      if (onSuccess) onSuccess();
      onHide();
    } catch (error) {
      console.error("Error updating site:", error);
      toast.error(translate("Error updating site. Please try again."), { position: "bottom-right", autoClose: 3000 });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal show={show} onHide={onHide}>
      <Modal.Header closeButton>
        <Modal.Title>{translate("Edit Site")}</Modal.Title>
      </Modal.Header>
      <Form onSubmit={handleSubmit}>
        <Modal.Body>
          <Form.Group className="mb-3">
            <Form.Label>{translate("Site Name")}</Form.Label>
            <Form.Control
              type="text"
              id="name"
              value={formData.name}
              onChange={handleChange}
              required
            />
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>{translate("Location")}</Form.Label>
            <Form.Control
              type="text"
              id="location"
              value={formData.location}
              onChange={handleChange}
              required
            />
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={onHide} disabled={loading}>
            {translate("Cancel")}
          </Button>
          <Button variant="primary" type="submit" disabled={loading}>
            {loading ? translate("Updating...") : translate("Update Site")}
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
};

export default ModalEditSite;